/**
 * Extracts top-level tokens from the given string, optionally including those found within HTML attributes.
 * Top-level tokens are enclosed in double curly braces `{{ }}`, with nested tokens' structure preserved.
 *
 * @param {String} input - The string to extract tokens from.
 * @param {Boolean} includeHtmlAttributes - Whether to include tokens found within HTML attributes.
 * @return {String[]} An array of top-level tokens found within the input string, based on the specified criteria.
 */
const getTokenIDsFromString = ( input, includeHtmlAttributes = false ) => {
	const tokens = [];
	let depth = 0;
	let currentToken = '';
	let collect = false;
	let insideHtmlAttribute = false;

	for ( let i = 0; i < input.length; i++ ) {
		const char = input[i];
		const nextChar = i + 1 < input.length ? input[i + 1] : '';

		// Check if we're entering or exiting an HTML attribute
		if ( ! collect && char === '=' && nextChar === '"' ) {
			insideHtmlAttribute = true;
			i++; // Skip the quote character
		} else if ( insideHtmlAttribute && char === '"' ) {
			insideHtmlAttribute = false;
			continue; // Move to the next character
		}

		if ( char === '{' && nextChar === '{' ) {
			depth++;
			if ( depth === 1 ) {
				collect = true; // Start collecting when entering top-level token
			} else if ( depth > 1 ) {
				// Add opening curly braces for nested tokens
				currentToken += '{{';
			}
			i++; // Skip the next char as we've processed it
		} else if ( char === '}' && nextChar === '}' && collect ) {
			if ( depth > 1 ) {
				// Add closing curly braces for nested tokens
				currentToken += '}}';
			}
			depth--;
			if ( depth === 0 ) {
				collect = false; // Stop collecting when exiting top-level token
				// Conditionally add the token based on insideHtmlAttribute flag and parameter
				if ( ! insideHtmlAttribute || ( insideHtmlAttribute && includeHtmlAttributes ) ) {
					tokens.push( currentToken );
				}
				currentToken = '';
			}
			i++; // Skip the next char as we've processed it
		} else if ( collect ) {
			// Collect characters inside a token
			currentToken += char;
		}
	}

	return tokens;
};

export default getTokenIDsFromString;
